/* eslint-disable no-underscore-dangle */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/prop-types */
import i18next from "i18next";
import moment from "moment-timezone";
import fluxQuerys from "./analyticsCustomSearch/AnalyticsFluxQuery";

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
const PERIOD_LABELS = {
  hourly: "hourlyPeriod",
  daily: "dailyPeriod",
  monthly: "monthlyPeriod",
  detail: "detailPeriod",
};

const getCurrentLocale = () => (i18next?.language === "en" ? "en-GB" : "es-ES");

function PresenceDot({ val }) {
  if (val === undefined || val === null) return <>—</>;

  const style = {
    display: "inline-block",
    width: 20,
    height: 20,
    borderRadius: "50%",
    // backgroundColor: val > 0 ? "#22c55e" : "#ef4444",
    backgroundColor: val > 0 ? "#ef4444" : "#22c55e",
    verticalAlign: "middle",
  };

  const PresentDot = <span style={style} />;
  return PresentDot;
}

function formatNumberToStringWithSuf(value, suf = null) {
  if (value === undefined || value === null || Number.isNaN(Number(value))) return "—";

  const formatedNumberToString = new Intl.NumberFormat(getCurrentLocale(), {
    maximumFractionDigits: 2,
  }).format(Number(value));

  const valueToString = suf ? `${formatedNumberToString} ${suf}` : formatedNumberToString;

  return valueToString;
}

const METRICS = [
  {
    key: "presence",
    label: "presence",
    suffix: null,
    formatedValueToString: (value) => <PresenceDot val={value} />,
  },
  {
    key: "temperature",
    label: "temperature",
    suffix: "°C",
    formatedValueToString: (value) => formatNumberToStringWithSuf(value),
  },
  {
    key: "setpoint",
    label: "setpointTemp",
    suffix: "°C",
    formatedValueToString: (value) => formatNumberToStringWithSuf(value),
  },
  {
    key: "temperatureDifferential",
    label: "ΔT",
    suffix: "°C",
    formatedValueToString: (value) => formatNumberToStringWithSuf(value),
  },
  {
    key: "electricPower",
    label: "electricPower",
    suffix: "W",
    formatedValueToString: (value) => formatNumberToStringWithSuf(value),
  },
  {
    key: "electricEnergy",
    label: "electricEnergy",
    suffix: "kWh",
    formatedValueToString: (value) => formatNumberToStringWithSuf(value),
  },
  {
    key: "thermalEnergyHeating",
    label: "thermalEnergyHeating",
    suffix: "kWh",
    formatedValueToString: (value) => formatNumberToStringWithSuf(value),
  },
  {
    key: "thermalEnergyCooling",
    label: "thermalEnergyCooling",
    suffix: "kWh",
    formatedValueToString: (value) => formatNumberToStringWithSuf(value),
  },
  {
    key: "gas",
    label: "gas",
    suffix: "m³",
    formatedValueToString: (value) => formatNumberToStringWithSuf(value),
  },
  {
    key: "coldWater",
    label: "coldWater",
    suffix: "m³",
    formatedValueToString: (value) => formatNumberToStringWithSuf(value),
  },
  {
    key: "hotWater",
    label: "hotWater",
    suffix: "m³",
    formatedValueToString: (value) => formatNumberToStringWithSuf(value),
  },
  {
    key: "custom1Incremental",
    label: "custom1Incremental",
    suffix: "kWh",
    formatedValueToString: (value) => formatNumberToStringWithSuf(value),
  },
  {
    key: "custom2Incremental",
    label: "custom2Incremental",
    suffix: "kWh",
    formatedValueToString: (value) => formatNumberToStringWithSuf(value),
  },
  {
    key: "custom1Linear",
    label: "custom1Linear",
    suffix: "m³",
    formatedValueToString: (value) => formatNumberToStringWithSuf(value),
  },
  {
    key: "custom2Linear",
    label: "custom2Linear",
    suffix: "m³",
    formatedValueToString: (value) => formatNumberToStringWithSuf(value),
  },
];

const INCREMENTAL_METRICS = [
  "electricEnergy",
  "thermalEnergyHeating",
  "thermalEnergyCooling",
  "gas",
  "coldWater",
  "hotWater",
  "custom1Incremental",
  "custom2Incremental",
];

const CUSTOM_METRICS = [
  "custom1Incremental",
  "custom2Incremental",
  "custom1Linear",
  "custom2Linear",
];

function getMetricSuffix(metricKey, metricsConfig = {}) {
  const configuredSuffix = metricsConfig?.[`${metricKey}Suffix`];
  if (configuredSuffix) return configuredSuffix.trim();

  const defaultSuffix = METRICS.find((metric) => metric?.key === metricKey)?.suffix;
  return defaultSuffix;
}

function getMetricCustomLabel(metricKey, metricsConfig = {}) {
  if (!CUSTOM_METRICS.includes(metricKey)) return "";

  const configuredLabel = metricsConfig?.[`${metricKey}Label`];
  if (typeof configuredLabel !== "string") return "";

  return configuredLabel.trim();
}

function getMetricDisplayLabel({ metricKey, metricsConfig = {}, t }) {
  const customLabel = getMetricCustomLabel(metricKey, metricsConfig);
  if (customLabel) return customLabel;

  const defaultLabel = METRICS.find((metric) => metric?.key === metricKey)?.label;
  return t(defaultLabel);
}

function isMetricEnabled(metricKey, metricsConfig = {}) {
  const configuredEnabled = metricsConfig?.[`${metricKey}Enabled`];
  if (configuredEnabled === undefined || configuredEnabled === null) return true;

  return Boolean(configuredEnabled);
}

function getEnabledMetrics(metricsConfig = {}) {
  return METRICS.filter((metric) => isMetricEnabled(metric?.key, metricsConfig));
}

const toMomentInTimeZone = (dateValue, timeZone) => {
  const safeTimeZone = timeZone;
  if (moment.tz) return moment.tz(dateValue, safeTimeZone);
  return moment(dateValue);
};

const formatYearOnly = (date, timeZone) => toMomentInTimeZone(date, timeZone).format("YYYY");

const formatDateInTimeZone = (dateValue, timeZone, format) =>
  toMomentInTimeZone(dateValue, timeZone).format(format);

const normalizeTimestamp = (rawTs) => {
  if (rawTs === undefined || rawTs === null || rawTs === "") return "";
  if (rawTs instanceof Date) return rawTs.toISOString();
  if (typeof rawTs === "number") return new Date(rawTs).toISOString();

  const parsed = Date.parse(rawTs);
  return Number.isNaN(parsed) ? "" : new Date(parsed).toISOString();
};

const formatAnalyticsDate = ({ ts, queryPeriod, timeZone }) => {
  if (!ts) return "-";

  const dateAtProjectTimeZone = toMomentInTimeZone(ts, timeZone);

  if (!dateAtProjectTimeZone.isValid()) return "-";

  switch (queryPeriod) {
    case "hourly":
    case "detail":
      return dateAtProjectTimeZone.format("YYYY-MM-DD HH:mm");
    case "daily":
      return dateAtProjectTimeZone.format("YYYY-MM-DD");
    case "monthly":
      return dateAtProjectTimeZone.format("YYYY-MM");
    default:
      return dateAtProjectTimeZone.format("YYYY-MM-DD");
  }
};

const getYRelativeValue = (values) => {
  const presentValues = values.filter((value) => value !== null);
  if (!presentValues.length) {
    return () => null;
  }

  const minimumValue = Math.min(...presentValues);
  const maximumValue = Math.max(...presentValues);
  const valueRange = maximumValue - minimumValue;

  if (valueRange === 0) {
    return (value) => (value === null ? null : 100);
  }

  const formatValue = (value) =>
    value === null ? null : ((value - minimumValue) / valueRange) * 100;

  return formatValue;
};

const getTimeStampSimplified = (rawTimestamp) => {
  const normalizedTimestamp = normalizeTimestamp(rawTimestamp);
  if (!normalizedTimestamp) return "";

  const timestampDate = new Date(normalizedTimestamp);
  if (Number.isNaN(timestampDate.valueOf())) return "";

  timestampDate.setUTCSeconds(0, 0);
  return timestampDate.toISOString();
};

const getAxisDomainPadding = (pointsCount) => {
  let xDomainPadding = 25;
  const yDomainPadding = 15;
  if (pointsCount <= 2) xDomainPadding = 250;
  if (pointsCount < 5) xDomainPadding = 120;
  if (pointsCount < 10) xDomainPadding = 60;
  return { xDomainPadding, yDomainPadding };
};

const BAR_OFFSETS = [-0.18, 0.18];
const SERIES_VISUAL_Y_OFFSET_BY_INDEX = [0, 0.6];

const sortTimelineTimestamps = (timestamps) =>
  [...timestamps].sort((a, b) => Date.parse(a) - Date.parse(b));

const buildChartModel = (points) => {
  const sortedPoints = [...points].sort((a, b) => {
    const ta = new Date(a.timestamp).valueOf();
    const tb = new Date(b.timestamp).valueOf();
    if (ta !== tb) return ta - tb;
    return 0;
  });

  const ticks = [];

  const series = sortedPoints.map((point, idx) => {
    const label = String(point?.label);
    const x = idx + 1;

    ticks.push(x);

    return {
      x,
      y: Number(point?.value),
      state: point?.state ?? null,
      xLabel: label,
    };
  });

  return {
    series,
    ticks,
  };
};

const buildComparisonChartModel = ({ charts, metricsConfig, seriesColors }) => {
  const timelineTimestamps = new Set();
  const timestampToLabel = {};

  let hasAnyPresenceValue = false;

  const preparedCharts = charts.map((chart, seriesIdx) => {
    const pointsByTimestamp = new Map();

    const isPresenceValue = chart.measurement === "presence" && chart.field === "value";
    if (isPresenceValue) {
      hasAnyPresenceValue = true;
    }

    chart.points.forEach((point) => {
      const timestamp = getTimeStampSimplified(point?.timestamp);
      if (!timestamp) return;

      pointsByTimestamp.set(timestamp, point);
      timelineTimestamps.add(timestamp);

      if (!timestampToLabel[timestamp]) {
        timestampToLabel[timestamp] = point?.label;
      }
    });

    return {
      chart,
      seriesIdx,
      pointsByTimestamp,
      measurementSuffix: getMetricSuffix(chart.measurement, metricsConfig),
    };
  });

  const timeline = sortTimelineTimestamps(timelineTimestamps);

  const tickMap = {};
  timeline.forEach((timestamp, idx) => {
    tickMap[idx + 1] = timestampToLabel[timestamp];
  });

  const seriesList = preparedCharts.map(
    ({ chart, seriesIdx, pointsByTimestamp, measurementSuffix }) => {
      const seriesColor = hasAnyPresenceValue ? seriesColors[1] : seriesColors[seriesIdx];
      const alignedValues = timeline.map((timestamp) => {
        const point = pointsByTimestamp.get(timestamp);
        return point ? point.value : null;
      });
      const mapYToRelativeIndex = getYRelativeValue(alignedValues);
      const barOffset = BAR_OFFSETS[seriesIdx];

      const seriesData = timeline.map((timestamp, idx) => {
        const x = idx + 1;
        const point = pointsByTimestamp.get(timestamp);
        const pointValue = point ? point.value : null;
        const pointLabel = timestampToLabel[timestamp];
        let y = mapYToRelativeIndex(pointValue);
        const isBarrValue = chart?.measurement === "presence" && chart?.field === "value";
        y = isBarrValue ? (pointValue === 0 ? 10 : 90) : y;
        return {
          x,
          barX: x + barOffset,
          y,
          pointValue,
          state: point ? point.state ?? "" : "",
          pointLabel,
          measurement: chart.measurement,
          field: chart.field,
          seriesLabel: chart.title,
          suffix: measurementSuffix,
          color: seriesColor,
        };
      });

      return {
        id: chart.id,
        measurement: chart.measurement,
        field: chart.field,
        color: seriesColor,
        label: chart.compareLabel || chart.title,
        data: seriesData,
      };
    }
  );

  const yValues = seriesList
    .flatMap((serie) => serie.data.map((datum) => datum.y))
    .filter((value) => value !== null);

  let yDomain = [0, 100];
  if (yValues.length) {
    const minY = Math.min(...yValues);
    const maxY = Math.max(...yValues);
    if (minY === maxY) {
      yDomain = [minY - 5, maxY + 5];
    } else {
      const pad = (maxY - minY) * 0.01;
      yDomain = [minY - pad, maxY + pad];
    }
  }

  return {
    timeline,
    tickMap,
    seriesList,
    yDomain,
  };
};

const rowsToPoints = (rows, period, tz, t) => {
  if (!rows?.length) return [];

  return rows.map((r) => {
    const date = toMomentInTimeZone(r._time, tz);
    let label = "";
    if (period === "hourly") {
      label = date.format("HH:mm");
    } else if (period === "daily") {
      label = date.format("DD-MM");
    } else if (period === "monthly") {
      label = t(MONTHS[parseInt(date.format("M"), 10) - 1]);
    } else if (period === "detail") {
      label = date.format("HH:mm");
    }

    return {
      label,
      value: Number(r._value),
      timestamp: r._time,
      state: r?.state ?? "",
    };
  });
};

function buildSingleTitle({
  apartmentLabel,
  measurementLabel,
  period,
  referenceDate,
  selectedHour,
  timeZone,
  t,
  mode,
  startDate,
  endDate,
}) {
  if (mode === "byDates") {
    const startDisplay = formatDateInTimeZone(`${startDate}T00:00:00.000Z`, timeZone, "DD-MM-YYYY");
    const endDisplay = formatDateInTimeZone(`${endDate}T00:00:00.000Z`, timeZone, "DD-MM-YYYY");
    const periodLabel = t(period === "daily" ? "dailyPeriod" : "monthlyPeriod");

    const title = `${apartmentLabel} — ${measurementLabel} — ${periodLabel} — ${startDisplay} → ${endDisplay}`;
    return title;
  }

  const periodNice = PERIOD_LABELS[period] || period;

  let dateSuffix = "";
  if (referenceDate) {
    const date = new Date(referenceDate);

    if (period === "hourly") {
      const ddmmyyyy = toMomentInTimeZone(date, timeZone).format("YYYY-MM-DD");
      const hh = Number.isFinite(selectedHour) ? String(selectedHour).padStart(2, "0") : null;

      dateSuffix = hh ? ` — ${ddmmyyyy} — ${hh}:00` : ` — ${ddmmyyyy}`;
    } else if (period === "daily") {
      dateSuffix = ` — ${formatDateInTimeZone(date, timeZone, "MM-YYYY")}`;
    } else if (period === "monthly") {
      dateSuffix = ` — ${formatYearOnly(date, timeZone)}`;
    } else if (period === "detail") {
      const ddmmyyyy = toMomentInTimeZone(date, timeZone).format("YYYY-MM-DD");
      dateSuffix = ` — ${ddmmyyyy}`;
    }
  }

  return `${apartmentLabel} — ${measurementLabel} — ${t(periodNice)}${dateSuffix}`;
}

function tzOffsetMinutesAt(utcMillis, tz) {
  const date = new Date(utcMillis);

  const formatedDate = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const parts = Object.fromEntries(
    formatedDate.formatToParts(date).map((part) => [part?.type, part?.value])
  );

  const dateAsUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );

  return Math.round((dateAsUTC - utcMillis) / 60000);
}

function computeUtcRangeForLocalDay(referenceDate, tz) {
  const ref = new Date(referenceDate);

  const year = ref.getUTCFullYear();
  const month = ref.getUTCMonth();
  const date = ref.getUTCDate();

  const guessUTC = Date.UTC(year, month, date, 0, 0, 0);

  const tzOffsetMin = tzOffsetMinutesAt(guessUTC, tz);

  const startUTC = guessUTC - tzOffsetMin * 60_000;
  const stopUTC = startUTC + 24 * 60 * 60 * 1000;

  return {
    startISO: new Date(startUTC).toISOString(),
    stopISO: new Date(stopUTC).toISOString(),
  };
}

function computeUtcRangeForLocalMonth(referenceDate, tz) {
  const ref = new Date(referenceDate);

  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
  });

  const parts = Object.fromEntries(
    formatter.formatToParts(ref).map((part) => [part?.type, part?.value])
  );

  const localYear = Number(parts.year);
  const localMonthIndex = Number(parts.month) - 1;

  const startGuessUTC = Date.UTC(localYear, localMonthIndex, 1, 0, 0, 0);
  const nextMonthGuessUTC = Date.UTC(localYear, localMonthIndex + 1, 1, 0, 0, 0);

  const startOffsetMin = tzOffsetMinutesAt(startGuessUTC, tz);
  const nextMonthOffsetMin = tzOffsetMinutesAt(nextMonthGuessUTC, tz);

  const startUTC = startGuessUTC - startOffsetMin * 60_000;
  const stopUTC = nextMonthGuessUTC - nextMonthOffsetMin * 60_000;

  return {
    startISO: new Date(startUTC).toISOString(),
    stopISO: new Date(stopUTC).toISOString(),
  };
}

const extractRows = (res) => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.resp)) return res.resp;
  if (Array.isArray(res?.rows)) return res.rows;
  if (Array.isArray(res?.results)) return res.results.flatMap((r) => r.rows || []);
  return [];
};

function buildAllTitle({ measurementLabel, period, referenceDate, timeZone, t }) {
  const periodNice = PERIOD_LABELS[period] || period;

  let dateSuffix = "";
  if (referenceDate) {
    const d = new Date(referenceDate);
    if (period === "hourly") {
      const ddmmyyyy = toMomentInTimeZone(d, timeZone).format("YYYY-MM-DD");
      dateSuffix = ` — ${ddmmyyyy}`;
    } else if (period === "daily") {
      dateSuffix = ` — ${formatDateInTimeZone(d, timeZone, "MM-YYYY")}`;
    } else if (period === "monthly") {
      dateSuffix = ` — ${formatYearOnly(d, timeZone)}`;
    }
  }

  return `${t("allApartments")} — ${measurementLabel} — ${t(periodNice)}${dateSuffix}`;
}

const removeChart = (id, setCharts) => setCharts((prev) => prev.filter((c) => c.id !== id));

const PERIODS = [
  { key: "hourly", label: "hourlyPeriod" },
  { key: "daily", label: "dailyPeriod" },
  { key: "monthly", label: "monthlyPeriod" },
  { key: "detail", label: "detailPeriod" },
];

const getPeriodOptions = (isIndividual, mode) => {
  if (mode === "byDates") {
    return PERIODS.filter((p) => p.key === "daily" || p.key === "monthly");
  }
  return isIndividual ? PERIODS : PERIODS.filter((p) => p.key !== "detail");
};

const FIELD_CONFIG = {
  temperature: [
    { key: "value", label: "average" },
    { key: "min", label: "min" },
    { key: "max", label: "max" },
  ],

  setpoint: [
    { key: "value", label: "average" },
    { key: "min", label: "min" },
    { key: "max", label: "max" },
  ],

  temperatureDifferential: [
    { key: "value", label: "average" },
    { key: "min", label: "min" },
    { key: "max", label: "max" },
  ],

  electricPower: [
    { key: "value", label: "average" },
    { key: "min", label: "min" },
    { key: "max", label: "max" },
  ],

  electricEnergy: [
    { key: "value", label: "lastValue" },
    { key: "diff", label: "Δincrease" },
  ],

  thermalEnergyHeating: [
    { key: "value", label: "lastValue" },
    { key: "diff", label: "Δincrease" },
  ],

  thermalEnergyCooling: [
    { key: "value", label: "lastValue" },
    { key: "diff", label: "Δincrease" },
  ],

  custom1Incremental: [
    { key: "value", label: "lastValue" },
    { key: "diff", label: "Δincrease" },
  ],

  custom2Incremental: [
    { key: "value", label: "lastValue" },
    { key: "diff", label: "Δincrease" },
  ],

  custom1Linear: [
    { key: "value", label: "average" },
    { key: "min", label: "min" },
    { key: "max", label: "max" },
  ],

  custom2Linear: [
    { key: "value", label: "average" },
    { key: "min", label: "min" },
    { key: "max", label: "max" },
  ],

  gas: [
    { key: "value", label: "lastValue" },
    { key: "diff", label: "Δincrease" },
  ],

  coldWater: [
    { key: "value", label: "lastValue" },
    { key: "diff", label: "Δincrease" },
  ],

  hotWater: [
    { key: "value", label: "lastValue" },
    { key: "diff", label: "Δincrease" },
  ],

  presence: [
    { key: "minutes", label: "timeInPresence" },
    { key: "value", label: "binaryValue" },
  ],
};

function avaiblesOptionsInTypeDataBySelection(measurement, period, isIndividual) {
  if (!measurement) return [];

  // INDIVIDUAL + detail → datos RAW → solo "value"
  if (isIndividual && period === "detail") {
    return [{ key: "value", label: "value" }];
  }

  const avaiblesOptionsInTypeData = FIELD_CONFIG[measurement];

  return avaiblesOptionsInTypeData || [];
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toISODate(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

const naturalSort = (a, b) => {
  const regex = /^\d+/;
  const aStartsWithNumber = regex.test(a.label);
  const bStartsWithNumber = regex.test(b.label);

  if (aStartsWithNumber && bStartsWithNumber) {
    return a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" });
  }

  if (aStartsWithNumber) {
    return -1;
  }

  if (bStartsWithNumber) {
    return 1;
  }
  return a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
};

const naturalStringSort = (a, b) =>
  naturalSort({ label: String(a || "") }, { label: String(b || "") });

const sortPointsByTimestampAndApartment = (points) =>
  [...points].sort((a, b) => {
    const ta = normalizeTimestamp(a?.timestamp) || 0;
    const tb = normalizeTimestamp(b?.timestamp) || 0;
    if (ta !== tb) return ta - tb;

    return String(a?.apartment || "").localeCompare(String(b?.apartment || ""), "es");
  });

function formatSimpleValue(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "0";
  return String(Number(numericValue.toFixed(2)));
}

function formatAnalyticsTooltipValue({ value, measurement, field, measurementSuffix, t }) {
  const isPresence = measurement === "presence";

  if (isPresence && field === "value") {
    return value === 0 ? t("unoccupied") : t("occupied");
  }

  if (isPresence && field === "minutes") {
    return `${formatSimpleValue(value)} min`;
  }

  const suffix = measurementSuffix ? ` ${measurementSuffix}` : "";
  return `${formatSimpleValue(value)}${suffix}`;
}

function isRecoverTooltipState({ toolTip, showStateInTooltip }) {
  return showStateInTooltip && String(toolTip?.state ?? "").toLowerCase() === "recover";
}

function formatAnalyticsTooltipLabel({
  toolTip,
  showStateInTooltip,
  measurement,
  field,
  measurementSuffix,
  t,
}) {
  const base = formatAnalyticsTooltipValue({
    value: toolTip?.y,
    measurement,
    field,
    measurementSuffix,
    t,
  });

  if (!showStateInTooltip) return base;

  return `${base}\n${t("state")}: ${toolTip?.state ?? ""}`;
}

const isIncrementalMetric = (metric) => INCREMENTAL_METRICS.includes(metric);

const isPresenceBinarySeries = (series) =>
  series.measurement === "presence" && series.field === "value";

const shouldShowStateInComparisonTooltip = ({ queryPeriod, datum }) =>
  queryPeriod === "hourly" && datum.field === "diff" && isIncrementalMetric(datum.measurement);

const getSeriesVisualYOffset = (seriesIndex) => SERIES_VISUAL_Y_OFFSET_BY_INDEX[seriesIndex] || 0;

const getMaxSeriesVisualYOffset = () => Math.max(...SERIES_VISUAL_Y_OFFSET_BY_INDEX);

const getRenderedYValue = (yValue, seriesIndex) =>
  yValue === null ? null : yValue + getSeriesVisualYOffset(seriesIndex);

const buildComparisonTooltipLabel = ({ datum, queryPeriod, t }) => {
  const showStateInTooltip = shouldShowStateInComparisonTooltip({ queryPeriod, datum });

  const value = formatAnalyticsTooltipValue({
    value: datum.pointValue || 0,
    measurement: datum.measurement,
    field: datum.field,
    measurementSuffix: datum.suffix,
    t,
  });

  return `${value} ${showStateInTooltip && datum.state ? `(${datum.state})` : ""}`;
};

const getMetricTitleLabel = ({ metricKey, metricsConfig = {}, t }) => {
  const effectiveMeasurementLabel = getMetricDisplayLabel({ metricKey, metricsConfig, t });
  const effectiveSuffix = getMetricSuffix(metricKey, metricsConfig);
  return effectiveSuffix
    ? `${effectiveMeasurementLabel} (${effectiveSuffix})`
    : effectiveMeasurementLabel;
};

const getFluxIndividualApartment = (
  period,
  mode,
  startDate,
  endDate,
  referenceDateISO,
  measurement,
  individualApartmentId,
  field,
  tz
) => {
  let flux = "";

  if (mode === "byDates") {
    const startISO = new Date(`${startDate}T00:00:00.000Z`).toISOString();
    const stopRaw = new Date(`${endDate}T00:00:00.000Z`);
    stopRaw.setUTCDate(stopRaw.getUTCDate() + 1);
    const stopISO = stopRaw.toISOString();
    const influxType = period === "daily" ? "daily" : "monthly";

    flux = fluxQuerys.individualApartByDatesFlux({
      startISO,
      stopISO,
      type: influxType,
      measurement,
      individualApartmentId,
      field,
    });
  }

  if (mode === "standart") {
    if (period === "hourly") {
      // Horarios procesados para el día seleccionado (client_processed, type==hourly)
      const { startISO, stopISO } = computeUtcRangeForLocalDay(referenceDateISO, tz);
      flux = fluxQuerys.individualApartHourlyFlux({
        startISO,
        stopISO,
        measurement,
        individualApartmentId,
        field,
        tz,
      });
    }

    if (period === "daily") {
      // Diarios procesados para el mes seleccionado (client_processed, type==daily)
      const { startISO, stopISO } = computeUtcRangeForLocalMonth(referenceDateISO, tz);
      flux = fluxQuerys.individualApartByDatesFlux({
        startISO,
        stopISO,
        type: "daily",
        measurement,
        individualApartmentId,
        field,
      });
    }

    if (period === "monthly") {
      // Mensuales procesados para el año seleccionado (client_processed, type==monthly)
      flux = fluxQuerys.individualApartMonthlyFlux({
        referenceDateISO,
        measurement,
        individualApartmentId,
        field,
        tz,
      });
    }

    if (period === "detail") {
      // Datos RAW del día seleccionado (client, type==raw)
      const { startISO, stopISO } = computeUtcRangeForLocalDay(referenceDateISO, tz);
      flux = fluxQuerys.individualApartDetailFlux({
        startISO,
        stopISO,
        measurement,
        individualApartmentId,
        field,
      });
    }
  }
  return flux;
};

const getStartAndStopISOByDates = (startDate, endDate) => {
  const startNormalized = normalizeTimestamp(startDate);
  const endNormalized = normalizeTimestamp(endDate);

  if (!startNormalized || !endNormalized) {
    return { startISO: "", stopISO: "" };
  }

  const startRaw = new Date(startNormalized);
  const stopRaw = new Date(endNormalized);

  if (Number.isNaN(startRaw.getTime()) || Number.isNaN(stopRaw.getTime())) {
    return { startISO: "", stopISO: "" };
  }

  startRaw.setUTCHours(0, 0, 0, 0);
  stopRaw.setUTCHours(0, 0, 0, 0);
  stopRaw.setUTCDate(stopRaw.getUTCDate() + 1);

  const startISO = startRaw.toISOString();
  const stopISO = stopRaw.toISOString();

  return { startISO, stopISO };
};

const cleanFileTitle = (s = "data") =>
  String(s)
    .replace(/[\\:*?"<>|]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 120);

const handleDownload = async ({
  points = [],
  title = "",
  isPresence,
  measurement,
  field,
  queryPeriod,
  projectTimeZone,
  t,
}) => {
  const XLSX = await import("xlsx");

  const { saveAs } = await import("file-saver");

  const hasApartment = points.some((point) => point?.apartment);

  const isIncremental = isIncrementalMetric(measurement);
  const includeState = isIncremental && field === "diff" && queryPeriod === "hourly";

  const pointsSortedByDate = sortPointsByTimestampAndApartment(points);

  const rowsToExcel = pointsSortedByDate.map((point) => {
    const date = formatAnalyticsDate({
      ts: point?.timestamp,
      queryPeriod,
      allApartments: hasApartment,
      timeZone: projectTimeZone,
    });
    const normalizedTimestamp = normalizeTimestamp(point?.timestamp);
    const value = isPresence
      ? field === "value"
        ? point.value === 0
          ? t("free")
          : t("occupied")
        : point.value
      : formatSimpleValue(point?.value);

    if (hasApartment) {
      const apartment = point?.apartment;

      return {
        date,
        apartment,
        value,
        ...(includeState ? { state: point?.state ?? "" } : {}),
        timestamp: `${normalizedTimestamp}`,
      };
    }

    return {
      date,
      value,
      ...(includeState ? { state: point?.state ?? "" } : {}),
      timestamp: `${normalizedTimestamp}`,
    };
  });

  const headers = [
    t("date"),
    ...(hasApartment ? [t("apartment")] : []),
    isPresence && field !== "value" ? t("minutes") : t("value"),
    ...(includeState ? [t("state")] : []),
    `${t("timestamp")} (UTC)`,
  ];

  const ws = XLSX.utils.json_to_sheet([], { header: headers });

  XLSX.utils.sheet_add_aoa(ws, [headers], { origin: "A1" });

  XLSX.utils.sheet_add_json(ws, rowsToExcel, { origin: "A2", skipHeader: true });

  const colWidths = headers.map((h) => {
    if (h === t("value")) return { wch: 10 };
    if (h === t("state")) return { wch: 14 };
    if (h === t("timestamp")) return { wch: 26 };
    return { wch: 22 };
  });

  ws["!cols"] = colWidths;

  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, ws, "Data");

  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const cleanTitle = cleanFileTitle(title);

  saveAs(blob, `${cleanTitle}.xlsx`);
};

const formatValueToTable = (rawValue, measurement, field, effectiveSuffix, t) => {
  if (!Number.isFinite(rawValue)) return "";

  if (measurement === "presence" && field === "value") {
    return rawValue === 0 ? t("unoccupied") : t("occupied");
  }

  if (measurement === "presence" && field === "minutes") {
    return `${rawValue} min`;
  }

  return `${rawValue}${effectiveSuffix ? ` ${effectiveSuffix}` : ""}`;
};

const parseISODateText = (value) => {
  if (typeof value !== "string") return null;
  const text = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return null;

  const parsed = new Date(`${text}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  return text;
};

const commitTypedISODate = (rawValue, setter) => {
  const parsed = parseISODateText(rawValue);
  if (parsed) setter(parsed);
};

const buildReferenceDateByPeriod = (period, month, yearKey, dateDaily) => {
  let referenceDate;

  if (period === "detail") {
    return new Date(`${dateDaily}T00:00:00.000Z`).toISOString();
  }

  if (period === "hourly") {
    return new Date(`${dateDaily}T00:00:00.000Z`).toISOString();
  }
  if (period === "daily") {
    const [y, m] = month.split("-");
    return new Date(Date.UTC(Number(y), Number(m) - 1, 1)).toISOString();
  }

  if (period === "monthly") {
    return new Date(Date.UTC(Number(yearKey), 0, 1)).toISOString();
  }

  return referenceDate;
};

const getReferenceDate = (mode, period, month, yearKey, dateDaily) => {
  let referenceDate;

  if (mode === "byDates") {
    referenceDate = null;
  } else {
    referenceDate = buildReferenceDateByPeriod(period, month, yearKey, dateDaily);
  }

  return referenceDate;
};

const normalizeApartmentId = (rawApartmentId) => {
  const normalized = String(rawApartmentId ?? "")
    .split("@")
    .pop()
    .trim();
  return normalized || null;
};

const getApartmentModel = (apartmentId, apartment = {}) => {
  const rawApartmentId = apartment?.apartmentId ?? apartmentId ?? "";
  const normalizedApartmentId = normalizeApartmentId(rawApartmentId);
  const deviceId = apartment?.onnaId ?? "";

  return {
    // Campos de UI
    label: apartment?.name,
    value: rawApartmentId,
    // Campos de dominio
    apartmentId: rawApartmentId,
    normalizeApartmentId: normalizedApartmentId,
    // Alias para compatibilidad entre componentes
    deviceId,
    onnaId: deviceId,
  };
};

const getApartList = (apartments) => {
  const list = Object.entries(apartments || {})
    .map(([apartmentId, apartment]) => getApartmentModel(apartmentId, apartment))
    .filter((apartment) => apartment?.apartmentId && apartment?.label)
    .sort(naturalSort);
  return list;
};

const assignMetricValues = (baseRow, enabledMetrics, valuesToShow = {}) => {
  const row = { ...baseRow };

  enabledMetrics.forEach((measurement) => {
    row[measurement?.key] = valuesToShow[measurement?.key] ?? undefined;
  });

  return row;
};

const hasAnyMetricValue = (row, enabledMetrics) =>
  enabledMetrics.some(
    (measurement) => row[measurement?.key] !== undefined && row[measurement?.key] !== null
  );

const buildValuesByApartment = (responseRows) => {
  const valuesByApartment = new Map();

  responseRows.forEach((responseRow) => {
    const apartmentIdRow = responseRow?.apartmentId;
    const onnaIdRow = responseRow?.onnaId;
    const measurementRow = responseRow?._measurement;
    const valueRow = responseRow?._value;
    const timeRow = responseRow?._time ? new Date(responseRow._time).valueOf() : NaN;

    if (!apartmentIdRow || !onnaIdRow || !measurementRow || !Number.isFinite(timeRow)) return;

    if (!valuesByApartment.has(apartmentIdRow)) {
      valuesByApartment.set(apartmentIdRow, {
        valuesByOnnaId: new Map(),
        updatedAt: null,
      });
    }

    const currentApartmentValues = valuesByApartment.get(apartmentIdRow);

    if (!currentApartmentValues.valuesByOnnaId.has(onnaIdRow)) {
      currentApartmentValues.valuesByOnnaId.set(onnaIdRow, {
        meansurementsValuesToShow: {},
        meansurementsUpdatedAt: {},
        updatedAt: null,
      });
    }

    const currentOnnaValues = currentApartmentValues.valuesByOnnaId.get(onnaIdRow);
    const previousMeasurementTs = currentOnnaValues.meansurementsUpdatedAt[measurementRow];

    const canReplaceMeasurementValue =
      !Number.isFinite(previousMeasurementTs) || timeRow >= previousMeasurementTs;

    if (canReplaceMeasurementValue) {
      currentOnnaValues.meansurementsValuesToShow[measurementRow] = valueRow;
      currentOnnaValues.meansurementsUpdatedAt[measurementRow] = timeRow;
    }

    currentOnnaValues.updatedAt =
      currentOnnaValues.updatedAt === null
        ? timeRow
        : Math.max(currentOnnaValues.updatedAt, timeRow);

    currentApartmentValues.updatedAt =
      currentApartmentValues.updatedAt === null
        ? timeRow
        : Math.max(currentApartmentValues.updatedAt, timeRow);
  });

  return valuesByApartment;
};

const getPreferredOnnaValues = (apartmentValues, selectedOnnaId) => {
  if (!apartmentValues?.valuesByOnnaId || apartmentValues.valuesByOnnaId.size === 0) return null;

  if (selectedOnnaId && apartmentValues.valuesByOnnaId.has(selectedOnnaId)) {
    return apartmentValues.valuesByOnnaId.get(selectedOnnaId);
  }

  const valuesSortedByRecency = Array.from(apartmentValues.valuesByOnnaId.values()).sort(
    (a, b) => (b?.updatedAt || 0) - (a?.updatedAt || 0)
  );

  return valuesSortedByRecency[0] || null;
};

const helpers = {
  METRICS,
  CUSTOM_METRICS,
  rowsToPoints,
  buildAllTitle,
  buildSingleTitle,
  extractRows,
  toISODate,
  pad2,
  avaiblesOptionsInTypeDataBySelection,
  removeChart,
  getPeriodOptions,
  formatAnalyticsTooltipValue,
  isRecoverTooltipState,
  formatAnalyticsTooltipLabel,
  formatAnalyticsDate,
  naturalSort,
  naturalStringSort,
  sortPointsByTimestampAndApartment,
  isIncrementalMetric,
  isPresenceBinarySeries,
  shouldShowStateInComparisonTooltip,
  getSeriesVisualYOffset,
  getMaxSeriesVisualYOffset,
  getRenderedYValue,
  buildComparisonTooltipLabel,
  getMetricSuffix,
  getMetricCustomLabel,
  getMetricDisplayLabel,
  isMetricEnabled,
  getEnabledMetrics,
  getMetricTitleLabel,
  getAxisDomainPadding,
  buildChartModel,
  buildComparisonChartModel,
  getFluxIndividualApartment,
  getStartAndStopISOByDates,
  handleDownload,
  formatValueToTable,
  commitTypedISODate,
  getReferenceDate,
  getApartmentModel,
  getApartList,
  buildValuesByApartment,
  getPreferredOnnaValues,
  assignMetricValues,
  hasAnyMetricValue,
};

export default helpers;
